import type { InfiniteData } from '@tanstack/react-query';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { contextUsageQueryKey } from '@/api/endpoints/context-usage';
import type {
  TLGrantPermissionRequest,
  TLGrantPermissionResponse,
  TLPaginatedResponseProgram,
  TLProgramsPermission,
  TLProgramsRevokePermissionsRequest,
  TLProgramsRevokePermissionsResponse,
} from '@/api/gen';
import { exportConversationToWorkflow, summarizeConversation } from '@/api/gen/conversations';
import { cancelMessageStreaming, editMessage } from '@/api/gen/conversations-messages';
import {
  deleteQueuedMessage,
  listQueuedMessages,
  retryQueuedMessage,
  sendQueuedMessageImmediately,
  updateQueuedMessage,
} from '@/api/gen/conversations-queued-messages';
import type {
  EditMessagePathParameters,
  TLChatMessageResponse,
  TLEditMessageRequest,
  TLPaginatedResponseQueuedMessageItem,
  TLPreviewSearchResponse,
  TLQueuedMessageItem,
  TLQueuedMessageResponse,
  TLSendMessageRequest,
  TLSendMessageRequestContext,
  TLSuccessResponse,
  TLSummarizeConversationResponse,
  TLUpdateQueuedMessageRequest,
  TLWorkflowExportResponse,
} from '@/api/gen/tessera-backend-api.schemas';
import { useSelectedModelStore } from '@/components/features/conversation/store/selected-model-store';
import { useMutationNext } from '@/query';
import type { MutationOptionsNext } from '@/query/types';
import type {
  TLAICancelConversationRequest,
  TLAIConversationDetailsRequest,
  TLAIConversationDetailsResponse,
  TLAIConversationFilePreviewRequest,
  TLAIConversationFilePreviewResponse,
  TLAIConversationFilesRequest,
  TLAIConversationFilesResponse,
  TLAIConversationListRequest,
  TLAIConversationListResponse,
  TLAIConversationSession,
  TLAIDeleteAllConversationsRequest,
  TLAIDeleteAllConversationsResponse,
  TLAIDeleteConversationMessageRequest,
  TLAIDeleteConversationMessageResponse,
  TLAIDeleteConversationRequest,
  TLAIDeleteConversationResponse,
  TLAIForkConversationRequest,
  TLAIForkConversationResponse,
  TLAILinkConversationTemplateRequest,
  TLAILinkConversationWorkflowRequest,
  TLAIMessageFeedbackRequest,
  TLAIMessageFeedbackResponse,
  TLAIRenameConversationRequest,
  TLAIRenameConversationResponse,
} from '@/types/api';
import { getApiClient } from '@/utils/api-client';
import type { QueryOptions } from '@/utils/types';

export const conversationListQueryOptions = (
  { conversation_type }: TLAIConversationListRequest,
  options?: QueryOptions<TLAIConversationListResponse>,
) =>
  queryOptions({
    queryKey: ['conversation', 'list', conversation_type],
    queryFn: async () => {
      return (await getApiClient()).get<TLAIConversationListResponse>('/conversations/list', {
        urlParams: { query: { conversation_type } },
      });
    },
    ...options,
  });

export const useConversationsListQuery = (
  input: TLAIConversationListRequest,
  options?: QueryOptions<TLAIConversationListResponse>,
) => {
  return useQuery(conversationListQueryOptions(input, options));
};

export const conversationDetailsQueryOptions = (
  { conversation_id, last_updated }: TLAIConversationDetailsRequest,
  options?: QueryOptions<TLAIConversationDetailsResponse>,
) =>
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: ['conversation', 'details', conversation_id],
    queryFn: async () => {
      const response = await (
        await getApiClient()
      ).get<TLAIConversationDetailsResponse, unknown, true>('/conversations', {
        urlParams: { path: [conversation_id], query: { last_updated } },
        rawResponse: true,
      });

      const serverTime = response.headers['X-Server-Time'] ?? response.headers['x-server-time'];

      return {
        ...response.data,
        serverTime,
      };
    },
    ...options,
  });

export const useConversationsDetailsQuery = (
  input: TLAIConversationDetailsRequest,
  options?: QueryOptions<TLAIConversationDetailsResponse>,
) => {
  return useQuery(conversationDetailsQueryOptions(input, options));
};

export const useConversationsRenameMutation = (
  options?: MutationOptionsNext<TLAIRenameConversationResponse, Error, TLAIRenameConversationRequest>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversation_id, ...input }: TLAIRenameConversationRequest) => {
      return (await getApiClient()).post<TLAIRenameConversationResponse>('/conversations/rename', input, {
        urlParams: { path: [conversation_id] },
      });
    },
    invalidateQueries: [['conversation', 'list']],
    ...options,
  });
};

export const useConversationsLinkWorkflowMutation = (
  options?: MutationOptionsNext<TLAIConversationSession, Error, TLAILinkConversationWorkflowRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutationNext({
    mutationFn: async ({ conversation_id, workflow_id }: TLAILinkConversationWorkflowRequest) => {
      return (await getApiClient()).patch<TLAIConversationSession>(
        '/conversations',
        { workflow_id },
        { urlParams: { path: [conversation_id] } },
      );
    },
    invalidateQueries: [['conversation', 'list']],
    ...options,
    // Write the patched session into the cached list immediately: the save flow navigates right
    // after linking, and the destination's workflow-filtered list must include the chat without
    // waiting for the invalidation refetch. Upsert (not just map) because a freshly created
    // blank-canvas conversation is often not in the list cache yet — a map-only update would be a
    // silent no-op, and the workflow page would show the chat missing until staleTime lapses.
    // Scoped to the linked type's list so it never leaks into another type's cache; moved to the
    // front to match the backend's updated_date-desc ordering (linking bumps updated_date).
    // Composed after the options spread so a caller's onSuccess extends this instead of replacing it.
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<TLAIConversationListResponse>(
        ['conversation', 'list', variables.conversation_type],
        (old) => {
          const sessions = old?.sessions ?? [];
          const others = sessions.filter((s) => s.conversation_id !== data.conversation_id);
          return { ...old, sessions: [data, ...others] };
        },
      );
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useConversationsLinkTemplateMutation = (
  options?: MutationOptionsNext<TLAIConversationSession, Error, TLAILinkConversationTemplateRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutationNext({
    mutationFn: async ({ conversation_id, template_id }: TLAILinkConversationTemplateRequest) => {
      return (await getApiClient()).patch<TLAIConversationSession>(
        '/conversations',
        { template_id },
        { urlParams: { path: [conversation_id] } },
      );
    },
    invalidateQueries: [['conversation', 'list']],
    ...options,
    // Same immediate cache upsert as the workflow link — see the comment there.
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<TLAIConversationListResponse>(
        ['conversation', 'list', variables.conversation_type],
        (old) => {
          const sessions = old?.sessions ?? [];
          const others = sessions.filter((s) => s.conversation_id !== data.conversation_id);
          return { ...old, sessions: [data, ...others] };
        },
      );
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useConversationsDeleteMutation = (
  options?: MutationOptionsNext<TLAIDeleteConversationResponse, Error, TLAIDeleteConversationRequest>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversation_id }: TLAIDeleteConversationRequest) => {
      const result = await (
        await getApiClient()
      ).delete<TLAIDeleteConversationResponse>('/conversations/delete', {
        urlParams: { path: [conversation_id] },
      });
      // Drop the per-conversation model selection now that the conversation is gone.
      useSelectedModelStore.getState().clear(conversation_id);
      return result;
    },
    invalidateQueries: [['conversation', 'list']],
    ...options,
  });
};

export const useConversationsForkMutation = (
  options?: MutationOptionsNext<TLAIForkConversationResponse, Error, TLAIForkConversationRequest>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversation_id, ...input }: TLAIForkConversationRequest) => {
      return (await getApiClient()).post<TLAIForkConversationResponse>('/conversations', input, {
        urlParams: { path: [conversation_id, 'fork'] },
      });
    },
    invalidateQueries: [['conversation', 'list']],
    ...options,
  });
};

const FILE_PREVIEW_DEFAULT_LIMIT = 1000;

export const conversationFilePreviewQueryOptions = (
  {
    conversation_id,
    program_id,
    limit = FILE_PREVIEW_DEFAULT_LIMIT,
    offset = 0,
    search,
  }: TLAIConversationFilePreviewRequest,
  options?: QueryOptions<TLAIConversationFilePreviewResponse>,
) =>
  queryOptions({
    queryKey: ['conversation', 'file-preview', conversation_id, program_id, limit, offset, search],
    queryFn: async () => {
      return (await getApiClient()).get<TLAIConversationFilePreviewResponse>('/conversations', {
        urlParams: {
          path: [conversation_id, 'files', program_id, 'preview'],
          query: { limit, offset, search },
        },
      });
    },
    ...options,
  });

export const useConversationsFilePreviewQuery = (
  request: TLAIConversationFilePreviewRequest,
  options?: QueryOptions<TLAIConversationFilePreviewResponse>,
) => {
  const { conversation_id, program_id, limit = FILE_PREVIEW_DEFAULT_LIMIT } = request;
  return useQuery(conversationFilePreviewQueryOptions({ conversation_id, program_id, limit }, options));
};

// The hand-written TLAIConversationFilePreviewResponse lacks has_more / next_offset (the generated
// TLPreviewResponse carries them); augment here until those fields are added to the hand-written type.
export type PaginatedFilePreviewResponse = TLAIConversationFilePreviewResponse & {
  has_more?: boolean;
  next_offset?: number | null;
};

export const conversationFilePreviewInfiniteQueryOptions = (
  { conversation_id, program_id, limit = FILE_PREVIEW_DEFAULT_LIMIT, search }: TLAIConversationFilePreviewRequest,
  options?: { enabled?: boolean },
) =>
  infiniteQueryOptions<
    PaginatedFilePreviewResponse,
    Error,
    InfiniteData<PaginatedFilePreviewResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: ['conversation', 'file-preview-paged', conversation_id, program_id, limit, search],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (await getApiClient()).get<PaginatedFilePreviewResponse>('/conversations', {
        urlParams: {
          path: [conversation_id, 'files', program_id, 'preview'],
          query: { limit, offset: pageParam, search },
        },
      }),
    getNextPageParam: (lastPage) => (lastPage.has_more ? (lastPage.next_offset ?? undefined) : undefined),
    enabled: options?.enabled,
    staleTime: 60_000,
  });

export const useConversationsFilePreviewInfiniteQuery = (
  request: TLAIConversationFilePreviewRequest,
  options?: { enabled?: boolean },
) => useInfiniteQuery(conversationFilePreviewInfiniteQueryOptions(request, options));

export const conversationFilePreviewSearchQueryOptions = (
  { conversation_id, program_id, query }: { conversation_id: string; program_id: string; query: string },
  options?: { enabled?: boolean },
) =>
  queryOptions<TLPreviewSearchResponse>({
    queryKey: ['conversation', 'file-preview-search', conversation_id, program_id, query],
    queryFn: async () =>
      (await getApiClient()).get<TLPreviewSearchResponse>('/conversations', {
        urlParams: {
          path: [conversation_id, 'files', program_id, 'preview', 'search'],
          query: { query },
        },
      }),
    enabled: (options?.enabled ?? true) && query.length > 0,
    staleTime: 60_000,
  });

export const useConversationsFilePreviewSearchQuery = (
  request: { conversation_id: string; program_id: string; query: string },
  options?: { enabled?: boolean },
) => useQuery(conversationFilePreviewSearchQueryOptions(request, options));

export const conversationFilesQueryOptions = (
  { conversation_id }: TLAIConversationFilesRequest,
  options?: QueryOptions<TLAIConversationFilesResponse>,
) =>
  queryOptions({
    queryKey: ['conversation', 'files', conversation_id],
    queryFn: async () => {
      return (await getApiClient()).get<TLAIConversationFilesResponse>('/conversations', {
        urlParams: { path: [conversation_id, 'files'] },
      });
    },
    ...options,
  });

export const useConversationsFilesQuery = (
  input: TLAIConversationFilesRequest,
  options?: QueryOptions<TLAIConversationFilesResponse>,
) => {
  return useQuery(conversationFilesQueryOptions(input, options));
};

export const useConversationsDeleteAllMutation = (
  options?: MutationOptionsNext<TLAIDeleteAllConversationsResponse, Error, TLAIDeleteAllConversationsRequest>,
) => {
  return useMutationNext({
    mutationFn: async (_input: TLAIDeleteAllConversationsRequest) => {
      return (await getApiClient()).delete<TLAIDeleteAllConversationsResponse>('/conversations/delete-all');
    },
    invalidateQueries: [['conversation', 'list', 'CHAT']],
    ...options,
  });
};

export const useConversationsCancelMutation = (
  options?: MutationOptionsNext<TLSuccessResponse, Error, TLAICancelConversationRequest>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversation_id, message_id }: TLAICancelConversationRequest) => {
      // Streaming cancel: persists cancellation and schedules queue drain server-side.
      return cancelMessageStreaming({ conversationId: conversation_id, messageId: message_id });
    },
    ...options,
  });
};

export const useConversationsDeleteMessageMutation = (
  options?: MutationOptionsNext<TLAIDeleteConversationMessageResponse, Error, TLAIDeleteConversationMessageRequest>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversation_id, message_id }: TLAIDeleteConversationMessageRequest) => {
      return (await getApiClient()).delete<TLAIDeleteConversationMessageResponse>('/conversations', {
        urlParams: { path: [conversation_id, 'message', message_id] },
      });
    },
    invalidateQueries: (_data, variables) => [
      conversationDetailsQueryOptions({
        conversation_id: variables.conversation_id,
      }).queryKey,
      // Deleting a message (and everything after it) changes the composition the model sees;
      // deletion has no follow-up stream to reconcile it later, so invalidate directly here.
      contextUsageQueryKey(variables.conversation_id),
    ],
    toast: true,
    ...options,
  });
};

export type EditMessageInput = EditMessagePathParameters &
  TLEditMessageRequest & { context?: TLSendMessageRequestContext };

export const useConversationsEditMessageMutation = (
  options?: MutationOptionsNext<TLChatMessageResponse, Error, EditMessageInput>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversationId, messageId, ...body }: EditMessageInput) => {
      return editMessage({ conversationId, messageId }, body);
    },
    invalidateQueries: (_data, variables) => [
      conversationDetailsQueryOptions({
        conversation_id: variables.conversationId,
      }).queryKey,
      // Edit truncates the turn immediately; the regenerated turn's stream will invalidate this
      // again on completion, but this closes the stale window between the edit and that stream.
      contextUsageQueryKey(variables.conversationId),
    ],
    ...options,
  });
};

export type ProgramsQueryParams = {
  offset?: number;
  page_size?: number;
  search?: string;
} & QueryOptions<TLPaginatedResponseProgram>;

export const conversationUserProgramsQueryOptions = ({
  offset,
  page_size,
  search,
  ...options
}: ProgramsQueryParams = {}) =>
  queryOptions({
    queryKey: ['conversation', 'user-programs', offset, page_size, search],
    queryFn: async () => {
      return (await getApiClient()).get<TLPaginatedResponseProgram>('/conversations/programs/user', {
        urlParams: { query: { offset, page_size, search } },
      });
    },
    ...options,
  });

export const useConversationUserProgramsQuery = (options?: ProgramsQueryParams) => {
  return useQuery(conversationUserProgramsQueryOptions(options));
};

export const useConversationsFeedbackMutation = (
  options?: MutationOptionsNext<TLAIMessageFeedbackResponse, Error, TLAIMessageFeedbackRequest>,
) => {
  return useMutationNext({
    mutationFn: async ({ conversation_id, message_id, is_positive }: TLAIMessageFeedbackRequest) => {
      return (await getApiClient()).post<TLAIMessageFeedbackResponse>(
        '/conversations',
        { is_positive },
        {
          urlParams: {
            path: [conversation_id, 'messages', message_id, 'feedback'],
          },
        },
      );
    },
    ...options,
  });
};

// ── Export to Workflow V2 ───────────────────────────────────────────

export const useExportConversationToWorkflowV2 = (
  options?: MutationOptionsNext<TLWorkflowExportResponse, Error, string>,
) => {
  return useMutationNext({
    mutationFn: async (conversationId: string) => exportConversationToWorkflow({ conversationId }),
    ...options,
  });
};

// ── Export conversation to Skill ───────────────────────────────────

export type TLSkillExportResponse = {
  name: string;
  slug: string;
  description: string;
  skill_markdown: string;
  is_fallback: boolean;
};

export async function exportConversationToSkill(conversationId: string): Promise<TLSkillExportResponse> {
  const client = await getApiClient();
  return client.post<TLSkillExportResponse>('/conversations', undefined, {
    urlParams: { path: [conversationId, 'export', 'skill'] },
  });
}

export const useExportConversationToSkill = (options?: MutationOptionsNext<TLSkillExportResponse, Error, string>) => {
  return useMutationNext({
    mutationFn: exportConversationToSkill,
    ...options,
  });
};

// ── Dev Chat mutations ──────────────────────────────────────────────

export type SendMessageInput = TLSendMessageRequest;
export type SendMessageResponse = TLChatMessageResponse | TLQueuedMessageResponse;

export const useChatSendMessageMutation = (
  options?: MutationOptionsNext<SendMessageResponse, Error, SendMessageInput>,
) => {
  return useMutationNext({
    mutationFn: async (input: SendMessageInput) => {
      return (await getApiClient()).post<SendMessageResponse>('/conversations/messages', input);
    },
    // A send can add attachments — refetch this conversation's file list so the files panel /
    // pickers stay current. Cap math uses user-message attachments from conversation history.
    invalidateQueries: (_data, variables) => [
      ['conversation', 'list'],
      ['conversation', 'files', variables.conversation_id],
    ],
    ...options,
  });
};

export const conversationQueuedMessagesQueryOptions = (
  conversationId: string,
  options?: QueryOptions<TLPaginatedResponseQueuedMessageItem>,
) =>
  queryOptions({
    queryKey: ['conversation', 'queued-messages', conversationId],
    queryFn: () => listQueuedMessages({ conversationId }),
    enabled: !!conversationId,
    ...options,
  });

export const useConversationQueuedMessagesQuery = (
  conversationId: string,
  options?: QueryOptions<TLPaginatedResponseQueuedMessageItem>,
) => useQuery(conversationQueuedMessagesQueryOptions(conversationId, options));

export type UpdateQueuedMessageInput = {
  conversationId: string;
  queuedMessageId: string;
  body: TLUpdateQueuedMessageRequest;
};

export const useUpdateQueuedMessageMutation = (
  options?: MutationOptionsNext<TLQueuedMessageItem, Error, UpdateQueuedMessageInput>,
) =>
  useMutationNext({
    mutationFn: ({ conversationId, queuedMessageId, body }: UpdateQueuedMessageInput) =>
      updateQueuedMessage({ conversationId, queuedMessageId }, body),
    invalidateQueries: (_data, variables) => [
      conversationQueuedMessagesQueryOptions(variables.conversationId).queryKey,
    ],
    toast: { error: 'Failed to update queued message' },
    ...options,
  });

export type DeleteQueuedMessageInput = {
  conversationId: string;
  queuedMessageId: string;
};

export const useDeleteQueuedMessageMutation = (options?: MutationOptionsNext<void, Error, DeleteQueuedMessageInput>) =>
  useMutationNext({
    mutationFn: ({ conversationId, queuedMessageId }: DeleteQueuedMessageInput) =>
      deleteQueuedMessage({ conversationId, queuedMessageId }),
    invalidateQueries: (_data, variables) => [
      conversationQueuedMessagesQueryOptions(variables.conversationId).queryKey,
    ],
    toast: { error: 'Failed to delete queued message' },
    ...options,
  });

export type SendQueuedMessageImmediatelyInput = {
  conversationId: string;
  queuedMessageId: string;
};

export const useSendQueuedMessageImmediatelyMutation = (
  options?: MutationOptionsNext<TLChatMessageResponse, Error, SendQueuedMessageImmediatelyInput>,
) =>
  useMutationNext({
    mutationFn: ({ conversationId, queuedMessageId }: SendQueuedMessageImmediatelyInput) =>
      sendQueuedMessageImmediately({ conversationId, queuedMessageId }),
    invalidateQueries: (_data, variables) => [
      conversationQueuedMessagesQueryOptions(variables.conversationId).queryKey,
      conversationDetailsQueryOptions({ conversation_id: variables.conversationId }).queryKey,
    ],
    toast: { error: 'Failed to send queued message' },
    ...options,
  });

export type RetryQueuedMessageInput = {
  conversationId: string;
  queuedMessageId: string;
};

export const useRetryQueuedMessageMutation = (
  options?: MutationOptionsNext<TLQueuedMessageResponse, Error, RetryQueuedMessageInput>,
) =>
  useMutationNext({
    mutationFn: ({ conversationId, queuedMessageId }: RetryQueuedMessageInput) =>
      retryQueuedMessage({ conversationId, queuedMessageId }),
    invalidateQueries: (_data, variables) => [
      conversationQueuedMessagesQueryOptions(variables.conversationId).queryKey,
    ],
    toast: { error: 'Failed to retry queued message' },
    ...options,
  });

export const useSummarizeConversationMutation = (
  options?: MutationOptionsNext<TLSummarizeConversationResponse, Error, { conversation_id: string }>,
) => {
  return useMutationNext({
    mutationFn: ({ conversation_id }: { conversation_id: string }) =>
      summarizeConversation({ conversationId: conversation_id }),
    ...options,
  });
};

// Shared programs

export const conversationSharedProgramsQueryOptions = ({
  offset,
  page_size,
  search,
  ...options
}: ProgramsQueryParams = {}) =>
  queryOptions({
    queryKey: ['conversation', 'shared-programs', offset, page_size, search],
    queryFn: async () => {
      return (await getApiClient()).get<TLPaginatedResponseProgram>('/conversations/programs/shared', {
        urlParams: { query: { offset, page_size, search } },
      });
    },
    ...options,
  });

export const useConversationSharedProgramsQuery = (options?: ProgramsQueryParams) => {
  return useQuery(conversationSharedProgramsQueryOptions(options));
};

// Program permissions

export const conversationProgramPermissionsQueryOptions = (
  programId: string,
  options?: QueryOptions<TLProgramsPermission[]>,
) =>
  queryOptions({
    queryKey: ['conversation', 'program-permissions', programId],
    queryFn: async () => {
      return (await getApiClient()).get<TLProgramsPermission[]>('/conversations/programs', {
        urlParams: { path: [programId, 'permissions'] },
      });
    },
    enabled: !!programId,
    ...options,
  });

export const useConversationProgramPermissionsQuery = (
  programId: string,
  options?: QueryOptions<TLProgramsPermission[]>,
) => {
  return useQuery(conversationProgramPermissionsQueryOptions(programId, options));
};

type GrantProgramPermissionInput = { programId: string } & TLGrantPermissionRequest;

export const useGrantProgramPermissionMutation = (
  options?: MutationOptionsNext<TLGrantPermissionResponse, Error, GrantProgramPermissionInput>,
) => {
  return useMutationNext({
    mutationFn: async ({ programId, ...input }: GrantProgramPermissionInput) => {
      return (await getApiClient()).post<TLGrantPermissionResponse>('/conversations/programs', input, {
        urlParams: { path: [programId, 'permissions'] },
      });
    },
    invalidateQueries: [
      ['conversation', 'program-permissions'],
      ['conversation', 'shared-programs'],
    ],
    ...options,
  });
};

export const useRevokeProgramPermissionsMutation = (
  options?: MutationOptionsNext<TLProgramsRevokePermissionsResponse, Error, TLProgramsRevokePermissionsRequest>,
) => {
  return useMutationNext({
    mutationFn: async (input: TLProgramsRevokePermissionsRequest) => {
      return (await getApiClient()).post<TLProgramsRevokePermissionsResponse>(
        '/conversations/programs/permissions/revoke',
        input,
      );
    },
    invalidateQueries: [
      ['conversation', 'program-permissions'],
      ['conversation', 'shared-programs'],
    ],
    ...options,
  });
};
